import { colorPalettes, type ThemeName } from "./color";
import { space, fluidSpace } from "./space";
import { radius } from "./radius";
import { typography, typographyRoles, resolveTypographyRole } from "./typography";
import { motion } from "./motion";
import { elevation } from "./elevation";
import { layout } from "./breakpoints";

const toKebab = (key: string): string => key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

/**
 * Apply all design tokens as CSS custom properties to the given element.
 * Idempotent. Pure over the token objects; side-effectful only on the DOM target.
 *
 * Naming convention:
 *   --color-<group>-<key>          color.bg.canvas → --color-bg-canvas
 *   --space-<n>                    space[3]        → --space-3
 *   --space-fluid-<key>            fluidSpace.lg   → --space-fluid-lg
 *   --radius-<key>                 radius.md       → --radius-md
 *   --font-{family|size|weight|line-height|letter-spacing|optical}-<key>
 *   --duration-<key>, --easing-<key>
 *   --motion-<kebab-key>           motion.control.pressIn → --motion-press-in
 *   --ease-<kebab-key>             motion.easings.pressOut → --ease-press-out
 *   --elevation-<n>, --elevation-<n>-border
 *   --hit-area, --viewport-gutter
 */
export function applyTokenCssVars(target: HTMLElement, theme: ThemeName = "dark"): void {
    const set = (name: string, value: string | number): void => {
        target.style.setProperty(name, String(value));
    };

    set("--theme-name", theme);

    for (const [group, entries] of Object.entries(colorPalettes[theme])) {
        for (const [key, value] of Object.entries(entries as Record<string, string>)) {
            set(`--color-${group}-${key}`, value);
        }
    }

    for (const [key, value] of Object.entries(space)) {
        set(`--space-${key}`, value);
    }
    for (const [key, value] of Object.entries(fluidSpace)) {
        set(`--space-fluid-${key}`, value);
    }

    for (const [key, value] of Object.entries(radius)) {
        set(`--radius-${key}`, value);
    }

    for (const [key, value] of Object.entries(typography.family)) {
        set(`--font-family-${key}`, value);
    }
    for (const [key, value] of Object.entries(typography.scale)) {
        set(`--font-size-${key}`, value);
    }
    for (const [key, value] of Object.entries(typography.weights)) {
        set(`--font-weight-${key}`, value);
    }
    for (const [key, value] of Object.entries(typography.lineHeights)) {
        set(`--line-height-${key}`, value);
    }
    for (const [key, value] of Object.entries(typography.optical)) {
        set(`--letter-spacing-optical-${key}`, value);
    }
    for (const [key, value] of Object.entries(typography.caps)) {
        set(`--letter-spacing-caps-${key}`, value);
    }

    // Typography roles flatten into CSS variables so a non-React surface can
    // opt into a role with one selector.
    for (const role of Object.keys(typographyRoles) as (keyof typeof typographyRoles)[]) {
        const resolved = resolveTypographyRole(role);
        set(`--type-${role}-family`, resolved.fontFamily);
        set(`--type-${role}-size`, resolved.fontSize);
        set(`--type-${role}-weight`, resolved.fontWeight);
        set(`--type-${role}-leading`, resolved.lineHeight);
        set(`--type-${role}-tracking`, resolved.letterSpacing);
        set(`--type-${role}-transform`, resolved.textTransform);
    }

    for (const [key, value] of Object.entries(motion.durations)) {
        set(`--duration-${key}`, value);
    }
    for (const [key, value] of Object.entries(motion.easings)) {
        set(`--easing-${key}`, value);
        set(`--ease-${toKebab(key)}`, value);
    }
    for (const [key, value] of Object.entries(motion.control)) {
        set(`--motion-${toKebab(key)}`, value);
    }

    for (const [level, bundle] of Object.entries(elevation)) {
        set(`--elevation-${level}`, bundle.shadow);
        set(`--elevation-${level}-border`, bundle.border);
    }

    set("--hit-area", `${String(layout.hitArea)}px`);
    set("--viewport-gutter", `${String(layout.viewportGutter)}px`);
}

/**
 * Inject the global stylesheet: font fallback metrics, the two focus
 * treatments (ring for controls, edge + halo for text fields), form control
 * baselines, scrollbars, the hit-area affordance, overlay hooks, the busy
 * spinner, the skeleton pulse, and the reduced-motion collapse.
 *
 * Idempotent: tagged with `data-galileo-globals`.
 */
export function installGlobalStyles(): void {
    if (typeof document === "undefined") {
        return;
    }
    const tag = "data-galileo-globals";
    if (document.head.querySelector(`style[${tag}]`) !== null) {
        return;
    }

    const style = document.createElement("style");
    style.setAttribute(tag, "");
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);
}

const GLOBAL_STYLES = `
/* ── Typography fallback metrics ─────────────────────────────────────── */

@font-face {
    font-family: "Roboto Flex Fallback";
    src: local("Arial");
    size-adjust: 100.5%;
    ascent-override: 92%;
    descent-override: 24%;
    line-gap-override: 0%;
}

@font-face {
    font-family: "JetBrains Mono Fallback";
    src: local("Courier New");
    size-adjust: 95%;
    ascent-override: 98%;
    descent-override: 25%;
    line-gap-override: 0%;
}

/* ── Base ────────────────────────────────────────────────────────────── */

* {
    box-sizing: border-box;
}

html {
    background-color: var(--color-bg-canvas);
    color: var(--color-fg-default);
}

body {
    margin: 0;
    padding: 0;
    background-color: var(--color-bg-canvas);
    color: var(--color-fg-default);
    font-family: var(--font-family-system, system-ui, sans-serif);
    font-optical-sizing: auto;
    font-feature-settings: "liga" 1, "kern" 1;
    font-variation-settings: "GRAD" 10;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overscroll-behavior-y: none;
}

/* ── Focus: two treatments, keyboard only ────────────────────────────── */
/* Controls (buttons, rows, menu items, segments): a 2px ring in the focus
   color (accent at ~70%) with a 1px offset. The outline follows the
   element's own border-radius. Do not set border-radius here: this runtime
   stylesheet loads after the CSS modules and would replace each control's
   radius on focus. The ring arrives over --motion-focus and leaves at once,
   so a control that loses focus while an overlay opens never paints a
   fading ring under it. */
:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 1px;
}

/* Text fields rest on the hairline. The color lives here, not inline, so
   the focus rule below can change it. */
[data-focus-style="input"] {
    border-color: var(--color-border-subtle);
}
[data-focus-style="input"][data-error] {
    border-color: var(--color-accent-danger);
}

/* Text fields: no outline. The edge turns to the accent at ~55% and a 3px
   halo at ~14% surrounds it. Native fields and [data-focus-style="input"]
   share the rule. :focus-within covers a frame that hosts a bare field
   (a composer well); on a native field it equals :focus. */
input:focus-visible,
textarea:focus-visible,
select:focus-visible,
[data-focus-style="input"]:focus-visible,
[data-focus-style="input"]:focus-within {
    outline: none;
    border-color: var(--color-border-inputFocus);
    box-shadow: 0 0 0 3px var(--color-border-halo);
    transition:
        border-color var(--motion-focus) var(--ease-standard),
        box-shadow var(--motion-focus) var(--ease-standard);
}

[data-focus-style="input"][data-error]:focus-visible,
[data-focus-style="input"][data-error]:focus-within,
input[aria-invalid="true"]:focus-visible,
textarea[aria-invalid="true"]:focus-visible {
    border-color: var(--color-accent-danger);
    box-shadow: 0 0 0 3px var(--color-status-dangerBg);
}

/* A frame that hosts a focused button (a composer's Send control) yields to
   the button's own ring; two rings on one well read as an error. Kept as a
   separate rule so an engine without :has() drops only this override. */
[data-focus-style="input"]:focus-within:has(button:focus-visible) {
    border-color: var(--color-border-subtle);
    box-shadow: none;
}
[data-focus-style="input"][data-error]:focus-within:has(button:focus-visible) {
    border-color: var(--color-accent-danger);
}

/* Controls that draw a ring of their own opt out. */
[data-focus-style="custom"]:focus-visible {
    outline: none;
    box-shadow: none;
}

/* Focus that an overlay returned after a pointer interaction. Chrome keeps
   :focus-visible on a programmatic focus when the element that had focus
   matched it (a text field always does), so the trigger would light up after
   every dialog with an input. The attribute is set by the overlay's focus
   return and removed on the next blur. */
[data-focus-return]:focus-visible {
    outline-color: transparent;
    box-shadow: none;
}

/* ── Hit area ────────────────────────────────────────────────────────── */
/* Extends the pointer target of a small control to --hit-area without
   changing its visual size. The host must be positioned. */

[data-hit-area] {
    position: relative;
}

[data-hit-area]::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: max(100%, var(--hit-area, 40px));
    height: max(100%, var(--hit-area, 40px));
    transform: translate(-50%, -50%);
}

/* ── Visually hidden ─────────────────────────────────────────────────── */

[data-visually-hidden] {
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
}

/* ── Horizontal scroll row ───────────────────────────────────────────── */
/* Snapping pill rows with a hidden scrollbar and an edge fade. Set
   --scroll-row-fade to 0px to remove the fade. */

[data-scroll-row] {
    --scroll-row-fade: 24px;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    mask-image: linear-gradient(
        to right,
        transparent 0,
        black var(--scroll-row-fade),
        black calc(100% - var(--scroll-row-fade)),
        transparent 100%
    );
    -webkit-mask-image: linear-gradient(
        to right,
        transparent 0,
        black var(--scroll-row-fade),
        black calc(100% - var(--scroll-row-fade)),
        transparent 100%
    );
}

[data-scroll-row]::-webkit-scrollbar {
    display: none;
}

[data-scroll-row] > * {
    scroll-snap-align: start;
    flex-shrink: 0;
}

/* ── Overlay hooks ───────────────────────────────────────────────────── */

/* useModalLock sets the attribute and compensates any body scrollbar width
   with padding-right, so the lock never shifts layout. */
body[data-modal-open] {
    overflow: hidden;
}

[data-sheet] {
    padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* ── Busy spinner ────────────────────────────────────────────────────── */

@keyframes galileo-spin {
    to {
        transform: rotate(360deg);
    }
}

[data-spinner] {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-right-color: transparent;
    animation: galileo-spin 0.8s linear infinite;
}

/* ── Skeleton pulse ──────────────────────────────────────────────────── */
/* One opacity pulse for every skeleton block. Reduced motion holds it static. */

@keyframes galileo-skeleton-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
}

[data-skeleton] {
    animation: galileo-skeleton-pulse 1.6s ease-in-out infinite;
}

/* ── Form control baseline ───────────────────────────────────────────── */

input::placeholder,
textarea::placeholder {
    color: var(--color-fg-subtle);
    opacity: 1;
}

input,
textarea,
select,
button {
    font-family: var(--font-family-system, system-ui, sans-serif);
    font-optical-sizing: auto;
    font-feature-settings: "liga" 1, "kern" 1;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--color-fg-default);
    -webkit-box-shadow: 0 0 0px 1000px var(--color-bg-surface) inset;
    transition: background-color 5000s ease-in-out 0s;
}

/* ── Selection ───────────────────────────────────────────────────────── */

::selection {
    background: color-mix(in oklch, var(--color-accent-primary) 35%, transparent);
    color: var(--color-accent-onPrimary);
}

/* ── Scrollbars ──────────────────────────────────────────────────────── */

* {
    scrollbar-width: thin;
    scrollbar-color: var(--color-border-default) transparent;
}

*::-webkit-scrollbar {
    width: 10px;
    height: 10px;
}
*::-webkit-scrollbar-track {
    background: transparent;
}
*::-webkit-scrollbar-thumb {
    background-color: var(--color-border-default);
    border-radius: 9999px;
    border: 2px solid transparent;
    background-clip: padding-box;
}
*::-webkit-scrollbar-thumb:hover {
    background-color: var(--color-border-strong);
    background-clip: padding-box;
}

/* ── Reduced motion ──────────────────────────────────────────────────── */
/* Everything collapses to a 100ms crossfade on the vestibular-safe
   channels (mirrors motion.reduced.duration). Complements the React-side
   hooks in motion-react. The same rules apply under the OS preference and
   under the app-level html[data-motion="reduced"] attribute that the
   Settings toggle sets. */

@media (prefers-reduced-motion: reduce) {
${reducedMotionRules("")}
}

${reducedMotionRules('html[data-motion="reduced"] ')}
`;

function reducedMotionRules(scope: string): string {
    return `
    ${scope}*, ${scope}*::before, ${scope}*::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
        scroll-behavior: auto !important;
    }
    ${scope}*, ${scope}*::before, ${scope}*::after {
        transition-property: opacity, color, background-color, border-color !important;
        transition-duration: 100ms !important;
    }
    ${scope}[data-spinner] {
        animation-duration: 0.8s !important;
        animation-iteration-count: infinite !important;
    }
    ${scope}[data-skeleton] {
        animation: none !important;
    }`;
}
